import WRAP from "WRAP-JS";
import {
  configSatelliteDhGlobalIrht, configSatelliteDhGlobalIrtp, configSatelliteDhGlobalWv,
  configSatelliteLayerGlobalIrht,
  configSatelliteLayerGlobalIrtp, configSatelliteLayerGlobalWv
} from "./config/ConfigSatellite";
import { _orderBy, formatUTCDate, m2ft, parseUTCDate } from "../../../utils/FPEUtils";
import LegendSatellite from "../legend/LegendSatellite";
import { WX_ID_SATELLITE } from "../../../store/WeatherContentsState";

export const SATELLITE_TYPE_IRHT = 'IRHT';
export const SATELLITE_TYPE_IRHT_COLOR = 'IRHT_COLOR';
export const SATELLITE_TYPE_IRTP = 'IRTP';
export const SATELLITE_TYPE_IRTP_COLOR = 'IRTP_COLOR';
export const SATELLITE_TYPE_WV= 'WV';

const styles = {
  [SATELLITE_TYPE_IRHT_COLOR]: 'default',
  [SATELLITE_TYPE_IRHT]: 'monochrome',
  [SATELLITE_TYPE_IRTP_COLOR]: 'default',
  [SATELLITE_TYPE_IRTP]: 'monochrome',
  [SATELLITE_TYPE_WV]: 'default',
};

class Satellite {
  static NAME = WX_ID_SATELLITE;
  dataType;
  changeDataTypeFlg = false;
  ready = false;
  dhGlobalIrht;
  dhGlobalIrtp;
  dhGlobalWv;
  layerGlobalIrht;
  layerGlobalIrtp;
  layerGlobalWv;
  etd;
  elapsed = 0;
  dispatcher;
  visible;

  typeInitialized = {
    [SATELLITE_TYPE_IRHT_COLOR]: false,
    [SATELLITE_TYPE_IRHT]: false,
    [SATELLITE_TYPE_IRTP_COLOR]: false,
    [SATELLITE_TYPE_IRTP]: false,
    [SATELLITE_TYPE_WV]: false
  };

  typeValidTimeInfo = {
    [SATELLITE_TYPE_IRHT_COLOR]: '---',
    [SATELLITE_TYPE_IRHT]: '---',
    [SATELLITE_TYPE_IRTP_COLOR]: '---',
    [SATELLITE_TYPE_IRTP]: '---',
    [SATELLITE_TYPE_WV]: '---'
  };

  constructor() {
    this.dataType = SATELLITE_TYPE_IRHT_COLOR;

    this.layerGlobalIrht = new WRAP.Geo.Layer('SATELLITE_GLOBAL_IRHT');
    this.layerGlobalIrtp = new WRAP.Geo.Layer('SATELLITE_GLOBAL_IRTP');
    this.layerGlobalWv = new WRAP.Geo.Layer('SATELLITE_GLOBAL_WV');
    this.layerGlobalIrht.setVisible(false);
    this.layerGlobalIrtp.setVisible(false);
    this.layerGlobalWv.setVisible(false);
    this.layerGlobalIrht.setTooltip(this._tooltipIrht);
    this.layerGlobalIrht.setRespondable(true);
  }

  setDispatcher(dispatcher) {
    this.dispatcher = dispatcher;
  }

  async initialize(dataType) {
    await this.initData(dataType);
    this.initLayer(dataType);
    this.ready = true;
  }

  initData(dataType) {
    return new Promise(resolve => {

      let dh;

      switch (dataType) {
        case SATELLITE_TYPE_IRHT_COLOR:
        case SATELLITE_TYPE_IRHT:
          dh = this.dhGlobalIrht = new WRAP.Data(configSatelliteDhGlobalIrht);
          this.typeInitialized[SATELLITE_TYPE_IRHT_COLOR] = true;
          this.typeInitialized[SATELLITE_TYPE_IRHT] = true;
          break;
        case SATELLITE_TYPE_IRTP_COLOR:
        case SATELLITE_TYPE_IRTP:
          dh = this.dhGlobalIrtp = new WRAP.Data(configSatelliteDhGlobalIrtp);
          this.typeInitialized[SATELLITE_TYPE_IRTP_COLOR] = true;
          this.typeInitialized[SATELLITE_TYPE_IRTP] = true;
          break;
        case SATELLITE_TYPE_WV:
          dh = this.dhGlobalWv = new WRAP.Data(configSatelliteDhGlobalWv);
          this.typeInitialized[SATELLITE_TYPE_WV] = true;
          break;
        default:
      }

      dh.updated('validtime', (ref) => {
        this._selectValidTime(ref, dataType);
        resolve(ref);
      });
    });
  }

  initLayer(dataType) {
    switch (dataType) {
      case SATELLITE_TYPE_IRHT_COLOR:
      case SATELLITE_TYPE_IRHT:
        this.layerGlobalIrht.configure(this.dhGlobalIrht, configSatelliteLayerGlobalIrht);
        break;
      case SATELLITE_TYPE_IRTP_COLOR:
      case SATELLITE_TYPE_IRTP:
        this.layerGlobalIrtp.configure(this.dhGlobalIrtp, configSatelliteLayerGlobalIrtp);
        break;
      case SATELLITE_TYPE_WV:
        this.layerGlobalWv.configure(this.dhGlobalWv, configSatelliteLayerGlobalWv);
        break;
      default:
    }
  }

  async drawLayer(visible) {
    this.visible = visible;
    if (!this.typeInitialized[this.dataType]) {
      await this.initialize(this.dataType);
    }
    this.setVisible(this.visible);
  }

  getLayer() {
    return this._allLayers();
  }

  setVisible(visible, type) {
    if (!this.typeInitialized[this.dataType]) {
      return;
    }

    const dh = this._getTargetDh(type);
    const layer = this._getTargetLayer(type);

    if (layer.visible() === visible) {
      return;
    }

    if (visible) {
      layer.show()
    } else {
      layer.hide();
    }

    dh.setAutoUpdate('validtime', visible);

    this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
  }

  getTimeInformation() {
    const valid = this.typeValidTimeInfo[this.dataType];
    const time = valid !== '---' ? formatUTCDate(parseUTCDate(valid), '%Y/%m/%d %H:%MZ') : valid;
    return {
      id: Satellite.NAME,
      label: 'SAT CLD',
      baseTime: time
    }
  }

  getLegend() {
    return {
      id: Satellite.NAME,
      label: 'SAT CLD',
      component: LegendSatellite,
      contents: this
    }
  }

  applyElapsed(elapsed) {
    this.elapsed = elapsed;

    if (!this.ready) return;

    const dh = this._getTargetDh();

    this._selectValidTime(dh.retained.validtime);
  }

  applyPlanETD(etd) {
    this.etd = etd;

    if (!this.ready) return;

    const dh = this._getTargetDh();

    this._selectValidTime(dh.retained.validtime);
  }

  getDataType() {
    return this.dataType;
  }

  async setDataType(dataType) {
    if (dataType === this.dataType) return;

    this.changeDataTypeFlg = true;

    if (!this.typeInitialized[dataType]) {
      await this.initialize(dataType);
    }

    const currentLayer = this._getTargetLayer(this.dataType);
    const newLayer = this._getTargetLayer(dataType);

    if (currentLayer === newLayer) {
      //同じLayerの場合、カラー・モノクロのstyleの切り替え
      currentLayer.setStyle(styles[dataType]);
      currentLayer.invalidate();
      this.dataType = dataType;
    } else {
      //現在のdataTypeを非表示
      this.setVisible(false);
      this.dataType = dataType;
      //切り替え後のdataTypeを表示
      this.setVisible(true);
      newLayer.setStyle(styles[dataType]);
      newLayer.invalidate();
    }
  }

  changeRoutePointEditing(routePointEditing) {
    this.layerGlobalIrht.disableInteraction(routePointEditing);
  }

  _selectValidTime(ref, dataType=this.dataType) {
    let timeList = ref;
    if (!timeList) return;
    timeList = timeList.validtime;

    let targetTime;
    if (!this.etd) {
      //プラン未選択時
      targetTime = Date.now();
    } else {
      //プラン選択時
      targetTime = this.etd.getTime() + this.elapsed * 1000;
    }

    const maxValid = timeList.sort()[timeList.length - 1];
    const filterTime = parseUTCDate(maxValid).getTime() - (5 * 60 * 60 * 1000);

    const tmpTimeList = timeList.reduce((accumulator ,validtime) => {
      const validSec = parseUTCDate(validtime).getTime();
      if (filterTime <= validSec) {
        accumulator.push(
          {
            "validtime": validtime,
            "validtime_msec": validSec
          }
        );
      }
      return accumulator;
    }, []);

    // targetTimeに最も近いvalidを選択する(直近が過去時制と未来時制で同時刻の場合、未来時制のValidデータを採用する。)
    const sortedTimelist = _orderBy(tmpTimeList, [
      o => {
        return Math.abs(targetTime - o.validtime_msec);
      },
      'validtime'
    ], ['asc', 'desc']);

    const selectedTime = sortedTimelist[0];

    const validTime = this.typeValidTimeInfo[dataType];

    if (selectedTime && (validTime !== selectedTime.validtime || this.changeDataTypeFlg)) {
      this._updateValidTimeInfo(dataType, selectedTime.validtime);
      this._updateDataset(dataType);
      this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
    }
  }

  _updateValidTimeInfo(dataType, validTime) {
    switch (dataType) {
      case SATELLITE_TYPE_IRHT_COLOR:
      case SATELLITE_TYPE_IRHT:
        this.typeValidTimeInfo[SATELLITE_TYPE_IRHT_COLOR] = validTime;
        this.typeValidTimeInfo[SATELLITE_TYPE_IRHT] = validTime;
        break;
      case SATELLITE_TYPE_IRTP_COLOR:
      case SATELLITE_TYPE_IRTP:
        this.typeValidTimeInfo[SATELLITE_TYPE_IRTP_COLOR] = validTime;
        this.typeValidTimeInfo[SATELLITE_TYPE_IRTP] = validTime;
        break;
      case SATELLITE_TYPE_WV:
        this.typeValidTimeInfo[SATELLITE_TYPE_WV] = validTime;
        break;
      default:
    }
  }

  _updateDataset(dataType) {
    const layer = this._getTargetLayer(dataType);
    if (this.typeValidTimeInfo[dataType] !== '---') {
      layer.set({
        validtime: this.typeValidTimeInfo[dataType]
      });
    }
  }

  _getTargetDh(dataType = this.dataType) {
    let dh;
    switch (dataType) {
      case SATELLITE_TYPE_IRHT:
      case SATELLITE_TYPE_IRHT_COLOR:
        dh = this.dhGlobalIrht;
        break;
      case SATELLITE_TYPE_IRTP:
      case SATELLITE_TYPE_IRTP_COLOR:
        dh = this.dhGlobalIrtp;
        break;
      case SATELLITE_TYPE_WV:
        dh = this.dhGlobalWv;
        break;
      default:
    }
    return dh;
  }

  _getTargetLayer(dataType = this.dataType) {
    let layer;
    switch (dataType) {
      case SATELLITE_TYPE_IRHT:
      case SATELLITE_TYPE_IRHT_COLOR:
        layer = this.layerGlobalIrht;
        break;
      case SATELLITE_TYPE_IRTP:
      case SATELLITE_TYPE_IRTP_COLOR:
        layer = this.layerGlobalIrtp;
        break;
      case SATELLITE_TYPE_WV:
        layer = this.layerGlobalWv;
        break;
      default:
    }
    return layer;
  }

  _tooltipIrht(geo) {
    const p = geo && geo.properties;
    if (p) {
      const val = p.data[0];
      if (val > 117) {
        // 160.57217 * (Val - 117) + 60.96で、階調値をmに変換
        let v1 = 160.57217 * (val - 117);
        v1 += 60.96;
        v1 = m2ft(v1);// mからftに変換
        return `${Math.round(v1)}ft`;
      }
    }
    return null;
  }

  _allLayers() {
    return [
      this.layerGlobalIrht,
      this.layerGlobalIrtp,
      this.layerGlobalWv
    ];
  }
}

export default Satellite;